import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import "@/styles/mobile.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Pages chargées immédiatement (essentielles au premier affichage)
import HomePage from "@/pages/HomePage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicPreview from "@/pages/PublicPreview";
import CodePromoRedeem from "@/pages/CodePromoRedeem";

// Lazy loading - pages chargées à la demande
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const HomeDashboard = lazy(() => import("@/pages/HomeDashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const OnboardingQuestionnaire = lazy(() => import("@/pages/OnboardingQuestionnaire"));
const Recommendations = lazy(() => import("@/pages/Recommendations"));
const ProgramDetail = lazy(() => import("@/pages/ProgramDetail"));
const AdminBackOffice = lazy(() => import("@/pages/AdminBackOffice"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const ProgramEditor = lazy(() => import("@/pages/ProgramEditor"));
const NewDashboard = lazy(() => import("@/pages/NewDashboard"));
const Account = lazy(() => import("@/pages/Account"));
const MyPrograms = lazy(() => import("@/pages/MyPrograms"));
const MySwing = lazy(() => import("@/pages/MySwing"));
const SubscriptionPlans = lazy(() => import("@/pages/SubscriptionPlans"));
const ImportData = lazy(() => import("@/pages/ImportData"));
const Statistics = lazy(() => import("@/pages/Statistics"));
const AddStatistics = lazy(() => import("@/pages/AddStatistics"));
const EditStatistics = lazy(() => import("@/pages/EditStatistics"));
const StatisticsDetail = lazy(() => import("@/pages/StatisticsDetail"));
const Videos = lazy(() => import("@/pages/Videos"));
const SeriesDetail = lazy(() => import("@/pages/SeriesDetail"));
const Practice = lazy(() => import("@/pages/Practice"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const TermsOfSale = lazy(() => import("@/pages/TermsOfSale"));
const Catalogue = lazy(() => import("@/pages/Catalogue"));

import Chatbot from "@/components/Chatbot";
import ScrollToTop from "@/components/ScrollToTop";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmationProvider, useConfirmation } from "@/contexts/ConfirmationContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";

// Composant de chargement pour Suspense - avec logo pour meilleure UX
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 gap-4">
    <img src="/logo-cabgolf.png" alt="CabGolf" className="w-16 h-16 animate-pulse" />
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function AppContent() {
  const { confirm } = useConfirmation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    
    // Register service worker for PWA with update checking
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered');
          
          // Check for updates every time the app loads
          registration.update();
          
          // If there's an update waiting, activate it immediately
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
          }
          
          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New service worker available, reloading...');
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            });
          });
        })
        .catch(err => console.log('Service Worker registration failed:', err));
    }
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment (Google OAuth)
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        await handleGoogleAuth(sessionId);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Check existing session
      const token = localStorage.getItem('session_token');
      if (token) {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        // Save user to localStorage for components that need it
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { session_id: sessionId });
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
    } catch (error) {
      console.error('Google auth failed:', error?.response?.data || error);
      // Show a visible error so users know why login didn't happen
      try {
        const { toast } = await import('sonner');
        toast.error(
          error?.response?.data?.detail
            || 'La connexion Google a échoué. Réessaie ou utilise ton mot de passe.',
        );
      } catch (_) { /* ignore */ }
    }
  };

  const logout = async () => {
    const confirmed = await confirm('Êtes-vous sûr de vouloir vous déconnecter ?');
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Component pour protéger les routes.
  // Note: l'ancien redirect vers /onboarding a été retiré — les utilisateurs vont directement
  // sur /dashboard après connexion, plus de questionnaire post-inscription obligatoire.
  const ProtectedRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Chatbot />
      <PWAInstallPrompt />
      <BrowserRouter future={{ v7_startTransition: true }}>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <HomePage user={user} setUser={setUser} />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/decouverte" element={<PublicPreview />} />
          <Route path="/code-promo" element={<CodePromoRedeem />} />
          <Route 
            path="/preferences" 
            element={
              <ProtectedRoute>
                <OnboardingQuestionnaire />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/recommendations" 
            element={<ProtectedRoute><Recommendations /></ProtectedRoute>} 
          />
          <Route 
            path="/program/:programId" 
            element={<ProtectedRoute><ProgramDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/account" 
            element={<ProtectedRoute><Account /></ProtectedRoute>} 
          />
          <Route 
            path="/my-programs" 
            element={<ProtectedRoute><MyPrograms /></ProtectedRoute>} 
          />
          <Route 
            path="/my-swing" 
            element={<ProtectedRoute><MySwing /></ProtectedRoute>} 
          />
          <Route 
            path="/admin" 
            element={
              user ? <AdminPanel /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/import" 
            element={<ImportData />} 
          />
          <Route 
            path="/admin/program/new" 
            element={
              user ? <ProgramEditor /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/admin/program/:programId" 
            element={
              user ? <ProgramEditor /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/admin-mapping" 
            element={
              user ? <AdminBackOffice /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/dashboard" 
            element={<ProtectedRoute><NewDashboard user={user} logout={logout} setUser={setUser} /></ProtectedRoute>} 
          />
          <Route 
            path="/statistics" 
            element={<ProtectedRoute><Statistics /></ProtectedRoute>} 
          />
          <Route 
            path="/statistics/add" 
            element={<ProtectedRoute><AddStatistics /></ProtectedRoute>} 
          />
          <Route 
            path="/statistics/edit/:sessionId" 
            element={<ProtectedRoute><EditStatistics /></ProtectedRoute>} 
          />
          <Route 
            path="/statistics/:sessionId" 
            element={<ProtectedRoute><StatisticsDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/videos" 
            element={<ProtectedRoute><Videos /></ProtectedRoute>} 
          />
          <Route 
            path="/series/:seriesId" 
            element={<ProtectedRoute><SeriesDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/catalogue" 
            element={<ProtectedRoute><Catalogue /></ProtectedRoute>} 
          />
          <Route 
            path="/programs" 
            element={<ProtectedRoute><Dashboard user={user} logout={logout} setUser={setUser} /></ProtectedRoute>} 
          />
          <Route 
            path="/training" 
            element={<ProtectedRoute><MyPrograms /></ProtectedRoute>} 
          />
          <Route 
            path="/stats" 
            element={<ProtectedRoute><Statistics /></ProtectedRoute>} 
          />
          <Route 
            path="/practice" 
            element={<ProtectedRoute><Practice /></ProtectedRoute>} 
          />
          <Route 
            path="/subscription-plans" 
            element={<ProtectedRoute><SubscriptionPlans /></ProtectedRoute>} 
          />
          <Route 
            path="/payment-success" 
            element={<ProtectedRoute><PaymentSuccess user={user} setUser={setUser} /></ProtectedRoute>} 
          />
          
          {/* Pages légales - accessibles sans authentification */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/terms-of-sale" element={<TermsOfSale />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <ConfirmationProvider>
      <FeatureFlagsProvider>
        <AppContent />
      </FeatureFlagsProvider>
    </ConfirmationProvider>
  );
}

export default App;
